import isString from 'lodash/isString'
import isFunction from 'lodash/isFunction'
import isObject from 'lodash/isObject'
import { CALL_API, isRSAA } from 'redux-api-middleware'
import { browserHistory } from 'react-router'
import { setAuthHeader } from '../actions'
import { getAuth } from '../selectors'

export const HTTP_UNAUTHORIZED = 401
export const NOT_FOUND = 404

// This middleware intercepts redux api middleware actions and adds an
// Authorization header to them. It also mangages updating the Auth header when
// a new one is recived by an API response. If an API call fails with a 401
// unauthorized we redirect to '/login' page.
// redux-api middleware action structure doc:
// https://github.com/agraboso/redux-api-middleware#lifecycle

const addAuthHeader = ({ store, action }) => {
  const auth = getAuth(store.getState())
  if (auth.header) {
    return {
      Authorization: auth.header,
      ...action.headers,
    }
  }
  return action.headers
}

// intercept the success meta handler
// and update auth header if one was recieved
const addDispatchAuthOnSuccess = ({ successType, store }) => {
  if (isString(successType)) {
    return {
      type: successType,
      meta: (action, state, res) => {
        const auth = res.headers.get('Authorization')
        if (auth) {
          store.dispatch(setAuthHeader(auth))
        }
        return auth
      },
    }
  }
  return {
    type: successType.type,
    meta: (action, state, res) => {
      const auth = res.headers.get('Authorization')
      if (auth) {
        store.dispatch(setAuthHeader(auth))
      }
      if (isFunction(successType.meta)) {
        return successType.meta(action, state, res)
      } else if (isObject(successType.meta)) {
        return successType.meta
      }
    },
  }
}

// intercept the fail meta handler and redirect to login page if
// response returns unauthorized
const redirectOnAPIFailedUnauthorized = ({ failType, store }) => {
  const resetHeaderAndGotoLoginPage = () => {
    store.dispatch(setAuthHeader(''))
    const returnUrl = window.location.pathname + window.location.search
    browserHistory.replace(`/login?returnUrl=${returnUrl}`)
  }
  if (isString(failType)) {
    return {
      type: failType,
      meta: (action, state, res) => {
        if (res.status === HTTP_UNAUTHORIZED) {
          resetHeaderAndGotoLoginPage()
        }
        if (res.status === NOT_FOUND) {
          browserHistory.replace(`/404`)
        }
      },
    }
  }
  return {
    type: failType.type,
    meta: (action, state, res) => {
      if (res && res.status === HTTP_UNAUTHORIZED) {
        resetHeaderAndGotoLoginPage()
      }
      if (res.status === NOT_FOUND) {
        browserHistory.replace(`/404`)
      }
      if (isFunction(failType.meta)) {
        return failType.meta(action, state, res)
      } else if (isObject(failType.meta)) {
        return failType.meta
      }
    },
  }
}

const apiAuthMiddleware = store => next => action => {
  // only modify api actions
  if (isRSAA(action)) {
    // copy action
    const actionCopy = { ...action[CALL_API] }
    // copy types
    actionCopy.types = [...actionCopy.types]
    // add auth header
    actionCopy.headers = addAuthHeader({ store, action: actionCopy })
    // add API url if in production
    if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_API) {
      actionCopy.endpoint = process.env.REACT_APP_API + actionCopy.endpoint
    }
    // modify success and failure handlers
    actionCopy.types = [
      actionCopy.types[0],
      addDispatchAuthOnSuccess({
        store,
        successType: actionCopy.types[1],
      }),
      redirectOnAPIFailedUnauthorized({ store, failType: actionCopy.types[2] }),
    ]
    // pass modified action to next middleware
    return next({ [CALL_API]: actionCopy })
  }
  // pass action to next middleware without doing anything
  return next(action)
}

export default apiAuthMiddleware



// WEBPACK FOOTER //
// ./src/middleware/auth.js